import React, { useState, useEffect, useRef } from "react";
import useAlphabeticInput from "../hooks/useAlphabeticInput";
import useNumericInput from "../hooks/useNumericInput";
import aboutAPI from "../api/aboutApi";
import { showError, showSuccess } from "../utils/toastUtils";

const AlumniFormModal = ({ onSuccess }) => {
  useAlphabeticInput();
  useNumericInput();
  const fileInputRef = useRef(null);

  const INITIAL_FORM_DATA = {
    full_name: "",
    email: "",
    batch: "",
    profession: "",
    message: "",
    video_url: "",
    thumbnail: null,
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail = emailRegex.test(formData.email);
  const validate = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Please enter email address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.profession.trim()) {
      newErrors.profession = "Please enter your profession";
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = "Please enter your name";
    } else if (!/^[A-Za-z\s]+$/.test(formData.full_name.trim())) {
      newErrors.full_name = "Name can contain only letters and spaces";
    }
    if (!formData.batch.trim()) {
      newErrors.batch = "Please enter graduation batch";
    } else if (!/^\d{4}$/.test(formData.batch)) {
      newErrors.batch = "Batch must be exactly 4 digits";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Please enter message";
    }
    if (!formData.thumbnail) {
      newErrors.thumbnail = "Please upload image";
    }
    if (formData.video_url && !/^https?:\/\/.+/.test(formData.video_url)) {
      newErrors.video_url = "Please enter a valid URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const formDataToSend = new FormData();

    formDataToSend.append("full_name", formData.full_name);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("batch", formData.batch);
    formDataToSend.append("profession", formData.profession);
    formDataToSend.append("message", formData.message);
    formDataToSend.append("video_url", formData.video_url);
    formDataToSend.append("thumbnail", formData.thumbnail);

    setLoading(true);

    try {
      const res = await aboutAPI.alumni_form(formDataToSend);
      if (res.code === 200) {
        showSuccess(res.message);

        setFormData(INITIAL_FORM_DATA);
        setErrors({});

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        if (onSuccess) {
          onSuccess();
        }

        const modalElement = document.getElementById("alumniModal");

        if (modalElement) {
          const modal =
            window.bootstrap.Modal.getInstance(modalElement) ||
            new window.bootstrap.Modal(modalElement);

          modal.hide();
        }
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
  setFormData(INITIAL_FORM_DATA);
  setErrors({});

  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
};


  return (
    <form className="p-1" onSubmit={handleSubmit}>
      <div className="row g-2">
        <div className="col-md-6">
          <label className="form-label" type="text">
            Full Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control only-alphabets"
            name="full_name"
            placeholder="Full Name"
            value={formData.full_name}
            onChange={handleChange}
            required
          />
          {errors.full_name && (
            <span className="text-danger">{errors.full_name}</span>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Email Address <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            className="form-control"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            required
          />
          {errors.email && <span className="text-danger">{errors.email}</span>}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Batch <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control only-numeric"
            name="batch"
            maxLength={4}
            placeholder="2024"
            value={formData.batch}
            onChange={handleChange}
            required
          />
          {errors.batch && <span className="text-danger">{errors.batch}</span>}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            Profession <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control only-alphabets"
            name="profession"
            placeholder="Profession"
            value={formData.profession}
            onChange={handleChange}
            required
          />
          {errors.profession && (
            <span className="text-danger">{errors.profession}</span>
          )}
        </div>
        <div className="col-12">
          <label className="form-label">
            Upload Image <span className="text-danger">*</span> (Preferred
            landscape image only)
          </label>
          <input
            ref={fileInputRef}
            type="file"
            className="custom-file-input"
            name="image"
            accept="image/*"
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                thumbnail: e.target.files[0],
              }))
            }
          />
        </div>
        {formData.thumbnail && (
          <img
            src={URL.createObjectURL(formData.thumbnail)}
            alt="preview"
            loading="lazy"
            style={{ width: "100px", marginTop: "10px" }}
          />
        )}

        <div className="col-12">
          <label className="form-label">Video URL (Optional)</label>
          <input
            type="url"
            className="form-control"
            name="video_url"
            placeholder="https://youtube.com/..."
            value={formData.video_url}
            onChange={handleChange}
          />
          {errors.video_url && (
            <span className="text-danger">{errors.video_url}</span>
          )}
        </div>

        <div className="col-12">
          <label className="form-label">
            Your Message <span className="text-danger">*</span>
          </label>
          <textarea
            className="form-control"
            rows="4"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            placeholder="Share a short note about your journey..."
          ></textarea>
        </div>

        <div className="col-12 pt-3 d-flex justify-content-center">
          <button type="submit" className="mrv-btn">
            REGISTER AS ALUMNI
          </button>
        </div>
      </div>
    </form>
  );
};

export default AlumniFormModal;
